import { API_BASE_URL } from "../frontend_config";
import { auth } from "./supabase";

export const MODULE_1_ID = "module_1_doctor_sim";
const IDEMPOTENCY_KEY_HEADER = "Idempotency-Key";

const DEFAULT_ERROR_MESSAGES = {
  module1_active_session_conflict:
    "You already have an active case. Continue it instead.",
  session_not_found: "This session was not found, or you do not have access to it.",
  module1_session_state_conflict: "This session can no longer be edited.",
  module1_persistence_unavailable:
    "Session storage is temporarily unavailable. Please try again.",
  module1_external_dependency_failed:
    "Apollo was unable to complete this request. Please try again.",
  ground_truth_reveal_disabled:
    "Reveal Medical Condition is disabled for this deployment.",
  network_error: "Cannot reach Apollo backend. Please check your connection and retry.",
};

export class Module1ApiError extends Error {
  constructor({
    message,
    errorCode = "module1_request_failed",
    status = null,
    correlationId = null,
    details = null,
  }) {
    super(message);
    this.name = "Module1ApiError";
    this.errorCode = errorCode;
    this.status = status;
    this.correlationId = correlationId;
    this.details = details;
  }
}

export async function parseApiError(response) {
  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const detail = body?.detail;
  const structured = detail && typeof detail === "object" ? detail : null;
  const errorCode = structured?.error_code || `http_${response.status}`;
  const message =
    DEFAULT_ERROR_MESSAGES[errorCode] ||
    structured?.message ||
    (typeof detail === "string" ? detail : null) ||
    `Apollo request failed with status ${response.status}.`;

  return new Module1ApiError({
    message,
    errorCode,
    status: response.status,
    correlationId: structured?.correlation_id || null,
    details: body,
  });
}

export function normalizeApiError(error) {
  if (error instanceof Module1ApiError) {
    return error;
  }

  if (error?.name === "TypeError") {
    return new Module1ApiError({
      message: DEFAULT_ERROR_MESSAGES.network_error,
      errorCode: "network_error",
    });
  }

  return new Module1ApiError({
    message: error?.message || "Apollo request failed. Please try again.",
    errorCode: error?.errorCode || "module1_request_failed",
    status: error?.status || null,
    correlationId: error?.correlationId || null,
    details: error,
  });
}

export function buildModule1IdempotencyKey() {
  const cryptoSource =
    typeof window !== "undefined" && window.crypto ? window.crypto : null;

  if (cryptoSource && typeof cryptoSource.randomUUID === "function") {
    return `m1-${cryptoSource.randomUUID()}`;
  }

  if (cryptoSource && typeof cryptoSource.getRandomValues === "function") {
    const bytes = cryptoSource.getRandomValues(new Uint8Array(16));
    const hex = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0")
    ).join("");
    return `m1-${hex}`;
  }

  throw new Module1ApiError({
    message: "Browser cryptography is unavailable. Please use a supported browser.",
    errorCode: "module1_idempotency_unavailable",
  });
}

function idempotencyHeaders(idempotencyKey) {
  return idempotencyKey ? { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey } : {};
}

async function getAuthHeaders() {
  const { data, error } = await auth.getCurrentSession();
  if (error) {
    throw new Module1ApiError({
      message: "Your session could not be verified. Please sign in again.",
      errorCode: "auth_session_unavailable",
    });
  }

  const token = data?.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(path, options = {}) {
  try {
    const authHeaders = await getAuthHeaders();
    const headers = {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
      ...authHeaders,
    };

    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      throw await parseApiError(response);
    }

    if (response.status === 204) {
      return null;
    }

    return response.json();
  } catch (error) {
    throw normalizeApiError(error);
  }
}

export function getModule1Options() {
  return request(`/api/sessions/${MODULE_1_ID}/options`);
}

export function getModule1ActiveSession() {
  return request(`/api/sessions/${MODULE_1_ID}/active`);
}

export function getModule1History(limit = 50) {
  return request(`/api/sessions/${MODULE_1_ID}/history?limit=${encodeURIComponent(limit)}`);
}

export function getModule1SessionDetail(sessionId) {
  return request(`/api/sessions/${encodeURIComponent(sessionId)}/detail`);
}

export function createModule1Session() {
  return request("/api/sessions", {
    method: "POST",
    body: JSON.stringify({ module: MODULE_1_ID }),
  });
}

export function initModule1Session(sessionId, payload, { idempotencyKey } = {}) {
  return request(`/api/sessions/${encodeURIComponent(sessionId)}/init`, {
    method: "POST",
    headers: idempotencyHeaders(idempotencyKey),
    body: JSON.stringify({ payload }),
  });
}

export function submitModule1Turn(sessionId, userMessage, { idempotencyKey } = {}) {
  return request(`/api/sessions/${encodeURIComponent(sessionId)}/turn`, {
    method: "POST",
    headers: idempotencyHeaders(idempotencyKey),
    body: JSON.stringify({ user_message: userMessage }),
  });
}

export function orderModule1Test(sessionId, testOrder, { idempotencyKey } = {}) {
  return request(`/api/sessions/${encodeURIComponent(sessionId)}/test`, {
    method: "POST",
    headers: idempotencyHeaders(idempotencyKey),
    body: JSON.stringify({ test_order: testOrder }),
  });
}

export function endModule1Session(sessionId, { idempotencyKey } = {}) {
  return request(`/api/sessions/${encodeURIComponent(sessionId)}/end`, {
    method: "POST",
    headers: idempotencyHeaders(idempotencyKey),
    body: JSON.stringify({ payload: {} }),
  });
}

export function revealModule1Condition(sessionId, { idempotencyKey } = {}) {
  return request(`/api/sessions/${encodeURIComponent(sessionId)}/reveal-condition`, {
    method: "POST",
    headers: idempotencyHeaders(idempotencyKey),
  });
}
